import { navigation } from '@repo/api-kit/registration';
import { client as qontoClient } from '@repo/shared-kit/utils';

import { apiBaseURL } from 'register-partners-js/constants/hosts';

const ApiGetUserOrganizationsResponse =
  navigation.organization.firstparty.v1.ApiGetUserOrganizationsResponse;

interface NavigationOrganizationResponse {
  id: string;
  name: string;
  slug: string;
  under_company_creation: boolean;
  contract_signed_at: string;
}

interface NavigationApiResponse {
  organizations: NavigationOrganizationResponse[];
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  underRegistration: boolean;
  createdAt: string;
}

export interface NetworkManager {
  request<T = unknown>(url: string, options?: unknown): Promise<T>;
}

/**
 * @deprecated This function uses the injected networkManager for requests and should be replaced
 * by {@link fetchNavigationOrganizationsClient} where possible, which is the preferred approach.
 *
 * Fetches organizations from the navigation API endpoint.
 * This replaces the legacy AppOrganization Ember Data model.
 *
 * Field mappings from API response:
 * - name → name
 * - slug → slug
 * - under_company_creation → underRegistration
 * - contract_signed_at → createdAt
 * - id → id
 *
 * Note: The API only returns enabled organizations, so no filtering is needed.
 */
export async function fetchNavigationOrganizations(
  networkManager: NetworkManager
): Promise<Organization[]> {
  const response = await networkManager.request<NavigationApiResponse>(
    `${apiBaseURL}/navigation/v1/organizations`
  );

  // oxlint-disable-next-line typescript/no-unnecessary-condition
  const organizations = response.organizations || [];

  return organizations.map(org => ({
    id: org.id,
    name: org.name,
    slug: org.slug,
    underRegistration: org.under_company_creation,
    createdAt: org.contract_signed_at,
  }));
}

/**
 * Fetches organizations from the navigation API endpoint using qontoClient directly,
 * instead of relying on the injected networkManager abstraction used in {@link fetchNavigationOrganizations}.
 *
 * This function also utilizes the api-kit' {@link ApiGetUserOrganizationsResponse.fromJSON}
 * to strictly parse and validate the API response.
 *
 * Field mappings from API response:
 * - id → id
 * - name → name
 * - slug → slug
 * - under_company_creation → underRegistration
 *
 * Note: Only enabled organizations are returned by the API.
 */
export async function fetchNavigationOrganizationsClient() {
  const response = await qontoClient(`${apiBaseURL}/navigation/v1/organizations`, {
    method: 'GET',
  });

  const parsedResponse = ApiGetUserOrganizationsResponse.fromJSON(response);

  return parsedResponse.organizations.map(org => ({
    id: org.id,
    name: org.name,
    slug: org.slug,
    underRegistration: org.underCompanyCreation,
  }));
}
